import { SxProps, Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";

/** Figures only line up column-wise with tabular numerals. */
export const TABULAR_NUMERALS = { fontVariantNumeric: "tabular-nums" } as const;

/** What a figure shows while it has no value: not calculated yet, or nothing to state it on. */
export const NO_VALUE = "—";

export type Sx = SxProps<Theme>;

/** Formats a numeric value for display. */
export type NumberFormat = (value: number) => string;

/** The directions a figure may be coloured by. A level carries no tone. */
export type Tone = "favourable" | "adverse" | "neutral";

/** The tone of a signed figure where above zero is the good direction. */
export const signedTone = (value: number): Tone =>
  value > 0 ? "favourable" : value < 0 ? "adverse" : "neutral";

/** The one colour each tone may use; neutral stays in quiet ink. */
export const toneColors = (theme: Theme): { [tone in Tone]: string } => ({
  favourable: theme.palette.data.positive.dark,
  adverse: theme.palette.data.negative.dark,
  neutral: "text.secondary",
});

/**
 * How much of its colour the fill of a bar takes; the page shows through the
 * rest. A quiet bar stands back behind the one that is selected.
 */
export const BAR_FILL_ALPHA = 0.56;
export const QUIET_BAR_FILL_ALPHA = 0.28;

/**
 * The fill of what is gained where it is one part of a divided bar: see-through
 * like the bar of a gain, and without an outline, as the parts beside it have
 * none. A quiet fill tells a second kind of gain from the first.
 */
export const gainFill = (theme: Theme, quiet = false) =>
  alpha(
    theme.palette.data.positive.main,
    quiet ? QUIET_BAR_FILL_ALPHA : BAR_FILL_ALPHA,
  );

/**
 * How a bar of a signed figure is drawn: filled see-through with the colour of
 * its sign, and outlined in that colour's dark shade.
 */
export const signedBar = (theme: Theme, value: number, quiet = false) => {
  const fillAlpha = quiet ? QUIET_BAR_FILL_ALPHA : BAR_FILL_ALPHA;
  const { positive, negative } = theme.palette.data;
  const [fill, line] =
    value > 0
      ? [positive.main, positive.dark]
      : value < 0
        ? [negative.main, negative.dark]
        : [theme.palette.text.secondary, theme.palette.text.secondary];
  return {
    bgcolor: alpha(fill, fillAlpha),
    border: `1px solid ${quiet ? alpha(line, BAR_FILL_ALPHA) : line}`,
  };
};
